import React, { useMemo, useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatDateEU, formatError } from "@/components/shared/helpers";

// ─── Métadonnées par type de mission ──────────────────────────────────────
const MISSION_META = {
  turnover_complete: {
    label: "Turnover complet",
    emoji: "🟣",
    badgeClass: "bg-violet-500/20 text-violet-200 border-violet-400/40",
    cardClass: "border-violet-400/30",
  },
  cleaning_only: {
    label: "Ménage seul",
    emoji: "🔵",
    badgeClass: "bg-sky-500/20 text-sky-200 border-sky-400/40",
    cardClass: "border-sky-400/30",
  },
  deposit_only: {
    label: "Dépôt linge",
    emoji: "🟢",
    badgeClass: "bg-emerald-500/20 text-emerald-200 border-emerald-400/40",
    cardClass: "border-emerald-400/30",
  },
};

// Badge de quantité : vert si > 0, gris si 0
function QtyBadge({ value, label, testid }) {
  const positive = value > 0;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${positive ? "bg-emerald-500/15 text-emerald-200 border-emerald-400/40" : "bg-slate-500/10 text-slate-400 border-slate-500/30"}`}
    >
      <span className="font-medium">{label}</span>
      <span className="font-bold tabular-nums">×{value}</span>
    </span>
  );
}

function BoolBadge({ active, label, testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${active ? "bg-emerald-500/15 text-emerald-200 border-emerald-400/40" : "bg-slate-500/10 text-slate-400 border-slate-500/30"}`}
    >
      {active ? "✓" : "—"} {label}
    </span>
  );
}

function MissionCard({ row, idx }) {
  const missionType = row.mission_type || "cleaning_only";
  const meta = MISSION_META[missionType] || MISSION_META.cleaning_only;

  const slot = row.slot_label || "—";
  const isDepositOnly = missionType === "deposit_only";
  const isCleaningOnly = missionType === "cleaning_only";
  const sortieDate = !isDepositOnly ? row.date : null;
  const entreeDate = row.next_arrival_date || null;

  const perform = row.services_to_perform || {};
  const deposit = row.services_to_deposit || {};

  return (
    <div
      data-testid={`admin-listing-card-${idx}`}
      className={`group rounded-2xl border ${meta.cardClass} bg-white/[0.04] p-4 shadow-sm transition hover:bg-white/[0.06]`}
    >
      {/* Rappel ménage effectué en HAUT de carte (Cas 3 — Dépôt linge seul) */}
      {isDepositOnly && row.last_cleaning_date && (
        <div
          data-testid={`admin-listing-card-last-cleaning-top-${idx}`}
          className="mb-3 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100"
        >
          🧹 Ménage effectué le <strong>{formatDateEU(row.last_cleaning_date)}</strong>
        </div>
      )}

      {/* En-tête : date + nom logement + badge mission */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold text-slate-50" data-testid={`admin-listing-card-date-${idx}`}>{formatDateEU(row.date)}</span>
            <span className="text-sm text-slate-300" data-testid={`admin-listing-card-property-${idx}`}>· {row.property_name}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              data-testid={`admin-listing-card-mission-${idx}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${meta.badgeClass}`}
            >
              <span>{meta.emoji}</span>{meta.label}
            </span>
            {row.late_departure && (
              <span data-testid={`admin-listing-card-late-${idx}`} className="inline-flex items-center rounded-full border border-amber-400/40 bg-amber-500/15 px-2 py-0.5 text-xs text-amber-200">
                ⚠️ Départ tardif — sortie {row.departure_time}
              </span>
            )}
            {row.early_arrival && (
              <span data-testid={`admin-listing-card-early-${idx}`} className="inline-flex items-center rounded-full border border-amber-400/40 bg-amber-500/15 px-2 py-0.5 text-xs text-amber-200">
                ⏩ Arrivée anticipée — entrée {row.arrival_time}
              </span>
            )}
          </div>
        </div>
        <div className="text-right text-xs text-slate-300 space-y-1">
          <div>
            <span className="text-slate-400">Créneau</span> <span className="font-medium text-slate-100">{slot}</span>
          </div>
          {sortieDate && (
            <div data-testid={`admin-listing-card-sortie-date-${idx}`} className="text-slate-100">
              <span className="text-slate-400">Sortie</span> <span className="font-medium">{formatDateEU(sortieDate)}{row.departure_time ? ` · ${row.departure_time}` : ""}</span>
            </div>
          )}
          {entreeDate && (missionType === "turnover_complete" || isDepositOnly) && (
            <div data-testid={`admin-listing-card-entree-date-${idx}`} className="text-slate-100">
              <span className="text-slate-400">{isDepositOnly ? "Entrée" : "Entrée suivante"}</span> <span className="font-medium">{formatDateEU(entreeDate)}{row.arrival_time ? ` · ${row.arrival_time}` : ""}</span>
            </div>
          )}
          <div>
            <span className="text-slate-400">Keybox</span> <span className="font-mono text-slate-100">{row.keybox_code || "—"}</span>
          </div>
          {row.building_box_code && (
            <div>
              <span className="text-slate-400">Box</span> <span className="font-mono text-slate-100">{row.building_box_code}</span>
            </div>
          )}
          {!isDepositOnly && row.cleaning_minutes_est > 0 && (
            <div>
              <span className="text-slate-400">Temps</span> <span className="font-medium text-slate-100">{row.cleaning_minutes_est} min</span>
            </div>
          )}
        </div>
      </div>

      {/* Corps : 2 colonnes "À faire" / "À déposer" */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Colonne À faire sur place */}
        <div className="space-y-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
          <div className="text-xs uppercase tracking-wide text-slate-400">À faire sur place</div>
          {isDepositOnly ? (
            <div className="text-sm text-slate-300" data-testid={`admin-listing-card-deposit-only-label-${idx}`}>
              ℹ️ Mission de dépôt linge uniquement
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              <BoolBadge testid={`admin-listing-card-perform-cleaning-${idx}`} active={perform.cleaning} label="🧹 Ménage" />
              <BoolBadge testid={`admin-listing-card-perform-degreasing-${idx}`} active={!!perform.degreasing} label="🧽 Dégraissage" />
              <BoolBadge testid={`admin-listing-card-perform-hygiene-${idx}`} active={perform.hygiene_kit} label="🧴 Kit hygiène" />
              <BoolBadge
                testid={`admin-listing-card-perform-basket-${idx}`}
                active={perform.welcome_basket && perform.welcome_basket !== "none"}
                label={perform.welcome_basket === "premium" ? "🎁 Panier Premium" : perform.welcome_basket === "standard" ? "🎁 Panier Standard" : "🎁 Panier"}
              />
            </div>
          )}
        </div>

        {/* Colonne À déposer — masquée pour Cas 2 (Ménage seul) */}
        {!isCleaningOnly && (
          <div className="space-y-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
            <div className="text-xs uppercase tracking-wide text-slate-400">À déposer</div>
            <div className="flex flex-wrap gap-1.5">
              <QtyBadge testid={`admin-listing-card-kit-simple-${idx}`} value={deposit.linen_kits_simple || 0} label="🧺 Kit S" />
              <QtyBadge testid={`admin-listing-card-kit-double-${idx}`} value={deposit.linen_kits_double || 0} label="🧺 Kit D" />
              <QtyBadge testid={`admin-listing-card-bed-simple-${idx}`} value={deposit.beds_made_simple || 0} label="🛏️ Lit S" />
              <QtyBadge testid={`admin-listing-card-bed-double-${idx}`} value={deposit.beds_made_double || 0} label="🛏️ Lit D" />
              <BoolBadge testid={`admin-listing-card-deposit-hygiene-${idx}`} active={!!deposit.hygiene_kit} label="🧴 Kit hygiène" />
              <BoolBadge
                testid={`admin-listing-card-deposit-basket-${idx}`}
                active={deposit.welcome_basket && deposit.welcome_basket !== "none"}
                label={deposit.welcome_basket === "premium" ? "🎁 Premium" : deposit.welcome_basket === "standard" ? "🎁 Standard" : "🎁 Panier"}
              />
              {(deposit.puericulture_items || []).length > 0 && (
                <span className="inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-200">
                  👶 {deposit.puericulture_items.length} item(s)
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mention BAS de carte (Cas 2 — Ménage seul) : dépôt à prévoir séparément */}
      {isCleaningOnly && row.next_arrival_date && (
        <div
          data-testid={`admin-listing-card-deferred-deposit-${idx}`}
          className="mt-3 rounded-lg border border-blue-400/40 bg-blue-500/10 px-3 py-2 text-xs text-blue-100"
        >
          🗓️ Dépôt linge à prévoir pour le <strong>{formatDateEU(row.next_arrival_date)}</strong>
          <span className="text-blue-200/80"> (arrivée du prochain locataire)</span>
        </div>
      )}

      {/* Pied de carte : prochaine arrivée pour contexte + note */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-white/5 pt-3 text-xs">
        <div className="flex items-center gap-2">
          {row.next_arrival_date ? (
            <span data-testid={`admin-listing-card-next-arrival-${idx}`} className="text-slate-300">
              <span className="text-slate-400">Prochaine arrivée :</span> <span className="font-medium text-slate-100">{formatDateEU(row.next_arrival_date)}</span>
              {row.next_arrival_out_of_range && (
                <span className="ml-2 italic text-slate-400/80 text-[10px]">(hors plage)</span>
              )}
            </span>
          ) : (
            <span data-testid={`admin-listing-card-next-arrival-${idx}`} className="text-slate-400">
              Prochaine arrivée : —
            </span>
          )}
        </div>
        {row.notes_planning && (
          <div className="max-w-md text-right text-slate-300 italic" data-testid={`admin-listing-card-note-${idx}`}>
            📝 {row.notes_planning}
          </div>
        )}
      </div>

      {/* Bannière fenêtre serrée (warning orange, non bloquant) */}
      {row.schedule_conflict && (
        <div
          data-testid={`admin-listing-card-conflict-${idx}`}
          className="mt-3 rounded-lg border border-orange-400/50 bg-orange-500/15 px-3 py-2 text-sm text-orange-100"
        >
          {row.schedule_conflict_message || "⚠️ Fenêtre horaire serrée — vérifier la disponibilité de l'équipe."}
        </div>
      )}
    </div>
  );
}

export default function AdminListing({ properties, listingRows, setListingRows, listingFrom, setListingFrom, listingTo, setListingTo, listingPropertyFilter, setListingPropertyFilter }) {
  // ─── Interventions Blanchisserie/Dégraissage confirmées dans la période (AJOUT iter51) ─
  const [laundryInterventions, setLaundryInterventions] = useState([]);
  const loadListing = async () => {
    try {
      const [res, laundryRes] = await Promise.all([
        api.get("/admin/listing", { params: { from_date: listingFrom, to_date: listingTo } }),
        api.get("/admin/laundry-interventions", { params: { from_date: listingFrom, to_date: listingTo } }).catch(() => ({ data: [] })),
      ]);
      setListingRows(res.data);
      setLaundryInterventions(laundryRes.data || []);
      toast({ title: "Listing chargé", description: `${(res.data || []).length} mission(s) + ${(laundryRes.data || []).length} intervention(s) blanchisserie` });
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    }
  };

  // Auto-load des interventions au montage/changement de période
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/laundry-interventions", { params: { from_date: listingFrom, to_date: listingTo } });
        setLaundryInterventions(r.data || []);
      } catch { /* ignore */ }
    })();
  }, [listingFrom, listingTo]);

  const sendTest = async () => {
    try {
      await api.post("/admin/listing/send-test", null, { params: { from_date: listingFrom, to_date: listingTo } });
      toast({ title: "Email envoyé", description: "Email de test envoyé (si SMTP OK)." });
    } catch (e) {
      toast({ title: "Envoi impossible", description: formatError(e), variant: "destructive" });
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get("/admin/listing/export-pdf", { params: { from_date: listingFrom, to_date: listingTo }, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `listing_${listingFrom}_${listingTo}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast({ title: "Export PDF", description: "Fichier téléchargé." });
    } catch (e) {
      toast({ title: "Export impossible", description: formatError(e), variant: "destructive" });
    }
  };

  // ─── Filtrage et regroupement par date d'intervention ────────────────────
  const filteredRows = useMemo(() => {
    return (listingRows || []).filter((r) => listingPropertyFilter === "all" || r.property_id === listingPropertyFilter);
  }, [listingRows, listingPropertyFilter]);

  // Helpers de date (timezone Paris)
  const todayISO = useMemo(() => {
    const now = new Date();
    return new Intl.DateTimeFormat("fr-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  }, []);

  const endOfWeekISO = useMemo(() => {
    // Dimanche soir (fin de semaine, lundi=jour 1)
    const tz = "Europe/Paris";
    const now = new Date();
    const parts = new Intl.DateTimeFormat("fr-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", weekday: "long" }).formatToParts(now);
    const y = parts.find((p) => p.type === "year").value;
    const m = parts.find((p) => p.type === "month").value;
    const d = parts.find((p) => p.type === "day").value;
    // calcule via JS Date (UTC-safe) à partir du iso Paris
    const base = new Date(`${y}-${m}-${d}T12:00:00Z`);
    const day = base.getUTCDay(); // 0=dim
    const daysToSunday = day === 0 ? 0 : 7 - day;
    base.setUTCDate(base.getUTCDate() + daysToSunday);
    return base.toISOString().slice(0, 10);
  }, []);

  const stats = useMemo(() => {
    const totalMinutes = filteredRows.reduce((acc, r) => acc + (r.cleaning_minutes_est || 0), 0);
    const todayCount = filteredRows.filter((r) => r.date === todayISO).length;
    const weekCount = filteredRows.filter((r) => r.date >= todayISO && r.date <= endOfWeekISO).length;
    return { total: filteredRows.length, totalMinutes, todayCount, weekCount };
  }, [filteredRows, todayISO, endOfWeekISO]);

  // Tri par date d'intervention croissante + groupement par date (séparateur)
  const groupedByDate = useMemo(() => {
    const groups = {};
    filteredRows.forEach((r) => {
      const key = r.date;
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    });
    // Trie les missions internes au groupe par nom de logement pour stabilité
    Object.values(groups).forEach((arr) => arr.sort((a, b) => (a.property_name || "").localeCompare(b.property_name || "")));
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredRows]);

  return (
    <Card data-testid="admin-listing-card" className="border-white/10 bg-white/[0.08]">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <CardTitle data-testid="admin-listing-title" className="text-slate-100">Listing opérationnel</CardTitle>
          <div data-testid="admin-listing-subtitle" className="text-sm text-slate-300">
            Cartes-missions classées par date d'intervention réelle (la plus proche en premier).
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="admin-listing-load-button" className="rounded-full bg-emerald-500 text-white hover:bg-emerald-400" onClick={loadListing}>Charger</Button>
          <Button data-testid="admin-listing-send-test-button" variant="outline" className="rounded-full border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10" onClick={sendTest}>Envoyer test</Button>
          <Button data-testid="admin-listing-export-pdf-button" variant="outline" className="rounded-full border-rose-500/30 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20" onClick={exportPdf}>📄 Export PDF</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filtres */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1">
            <Label data-testid="admin-listing-from-label">Du</Label>
            <Input data-testid="admin-listing-from-input" type="date" className="border-white/10 bg-white/[0.08]" value={listingFrom} onChange={(e) => setListingFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label data-testid="admin-listing-to-label">Au</Label>
            <Input data-testid="admin-listing-to-input" type="date" className="border-white/10 bg-white/[0.08]" value={listingTo} onChange={(e) => setListingTo(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label data-testid="admin-listing-property-label">Logement</Label>
            <select data-testid="admin-listing-property-select" className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50" value={listingPropertyFilter} onChange={(e) => setListingPropertyFilter(e.target.value)}>
              <option value="all">Tous</option>
              {properties.filter((p) => !p.archived).map((p) => (<option key={p.id} value={p.id}>{p.short_name}</option>))}
            </select>
          </div>
        </div>

        {/* Statistiques en haut */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div data-testid="admin-listing-stat-total" className="rounded-xl border border-white/10 bg-white/[0.05] p-3">
            <div className="text-xs text-slate-300">Missions sur la période</div>
            <div className="mt-1 text-2xl font-semibold text-slate-50 tabular-nums">{stats.total}</div>
          </div>
          <div data-testid="admin-listing-stat-time" className="rounded-xl border border-white/10 bg-white/[0.05] p-3">
            <div className="text-xs text-slate-300">Temps estimé total</div>
            <div className="mt-1 text-2xl font-semibold text-slate-50 tabular-nums">{Math.floor(stats.totalMinutes / 60)}h{String(stats.totalMinutes % 60).padStart(2, "0")}</div>
          </div>
          <div data-testid="admin-listing-stat-today" className="rounded-xl border border-rose-400/30 bg-rose-500/10 p-3">
            <div className="text-xs text-rose-300">Aujourd'hui</div>
            <div className="mt-1 text-2xl font-semibold text-rose-100 tabular-nums">{stats.todayCount}</div>
          </div>
          <div data-testid="admin-listing-stat-week" className="rounded-xl border border-orange-400/30 bg-orange-500/10 p-3">
            <div className="text-xs text-orange-300">Cette semaine</div>
            <div className="mt-1 text-2xl font-semibold text-orange-100 tabular-nums">{stats.weekCount}</div>
          </div>
        </div>

        {/* Cartes regroupées par date d'intervention (séparateur "📅 JJ/MM/AAAA") */}
        {!groupedByDate.length && (
          <div data-testid="admin-listing-empty" className="rounded-xl border border-dashed border-white/10 bg-white/[0.03] py-10 text-center text-slate-300">
            Cliquez sur <strong>Charger</strong> pour générer le listing.
          </div>
        )}
        {groupedByDate.map(([dateKey, rows]) => (
          <div key={dateKey} className="space-y-3">
            <div data-testid={`admin-listing-date-group-${dateKey}`} className="flex items-center gap-2 pt-2">
              <div className="h-px flex-1 bg-white/10" />
              <div className="text-sm font-semibold uppercase tracking-wide text-slate-200">
                📅 {formatDateEU(dateKey)}
                {dateKey === todayISO && <span className="ml-2 rounded-full bg-rose-500/15 text-rose-200 border border-rose-400/30 px-2 py-0.5 text-[10px] uppercase">Aujourd'hui</span>}
              </div>
              <div className="h-px flex-1 bg-white/10" />
            </div>
            <div className="space-y-3">
              {rows.map((r, idx) => (
                <MissionCard key={`${r.date}-${r.stay_id}-${r.time_type}-${idx}`} row={r} idx={`${dateKey}-${idx}`} />
              ))}
            </div>
          </div>
        ))}

        {/* AJOUT iter51 — Section Interventions Blanchisserie/Dégraissage terrain */}
        {laundryInterventions.length > 0 && (
          <div data-testid="admin-listing-laundry-section" className="space-y-3 pt-4 border-t border-white/10">
            <div className="text-sm font-semibold uppercase tracking-wide text-emerald-200 flex items-center gap-2">
              🧺 Interventions Blanchisserie/Dégraissage
              <span className="text-xs text-slate-400 normal-case">({laundryInterventions.length})</span>
            </div>
            <div className="space-y-2">
              {laundryInterventions
                .filter((iv) => listingPropertyFilter === "all" || iv.property_id === listingPropertyFilter)
                .map((iv) => {
                  const typeLabel = iv.request_type === "degreasing" ? "Dégraissage"
                    : iv.request_type === "laundry_full" ? "Blanchisserie complète"
                    : "Blanchisserie ponctuelle";
                  return (
                    <div
                      key={iv.id}
                      data-testid={`admin-listing-laundry-${iv.id}`}
                      className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-100 flex items-center gap-2 flex-wrap">
                            {iv.property_short_name || iv.property_id}
                            <span className="rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 px-2 py-0.5 text-[10px] uppercase">{typeLabel}</span>
                            <span className="text-xs text-slate-400">le <b>{formatDateEU(iv.confirmed_date)}</b></span>
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">Propriétaire : {iv.owner_name}</div>
                          {iv.request_type === "laundry_full" && iv.articles?.length > 0 && (
                            <div className="text-xs text-slate-300 mt-1">
                              <b>Articles :</b> {iv.articles.map(a => `${a.name} × ${a.qty}`).join(", ")}
                            </div>
                          )}
                          {iv.request_type === "laundry_punctual" && iv.punctual_description && (
                            <div className="text-xs text-slate-300 mt-1"><b>Détail :</b> {iv.punctual_description}</div>
                          )}
                          {iv.admin_note && <div className="text-xs text-slate-400 mt-1 italic">Note : {iv.admin_note}</div>}
                        </div>
                        {iv.final_price_ttc != null && (
                          <div className="text-sm font-semibold text-emerald-300">{Number(iv.final_price_ttc).toFixed(2)}€</div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
